/**
 * Site-wide date/time — China mainland (Asia/Shanghai, UTC+8, no DST).
 * Use these helpers for all user-visible dates and admin date inputs.
 */
export const CHINA_TIMEZONE = 'Asia/Shanghai';

const CHINA_UTC_OFFSET_MS = 8 * 60 * 60 * 1000;

export const toChinaCalendarParts = (date: Date) => {
  const chinaMs =
    date.getTime() + date.getTimezoneOffset() * 60_000 + CHINA_UTC_OFFSET_MS;
  const c = new Date(chinaMs);
  return {
    year: c.getUTCFullYear(),
    month: c.getUTCMonth() + 1,
    day: c.getUTCDate(),
    hour: c.getUTCHours(),
    minute: c.getUTCMinutes(),
  };
};

export function getChinaYear(): number {
  return toChinaCalendarParts(new Date()).year;
}

/** Today (China) as YYYY-MM-DD for date inputs. */
export function getChinaTodayDateInput(): string {
  return formatChinaDateInput(new Date());
}

export function toValidTimestamp(value?: string | Date | null): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function compareDescByDate(
  a?: string | Date | null,
  b?: string | Date | null
): number {
  return toValidTimestamp(b) - toValidTimestamp(a);
}

export function formatChinaDate(
  value?: string | Date | null,
  locale = 'zh-CN',
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }
): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, { timeZone: CHINA_TIMEZONE, ...options });
}

/** YYYY-MM-DD for <input type="date"> in China calendar. */
export function formatChinaDateInput(value?: string | Date | null): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CHINA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** Parse YYYY-MM-DD as 00:00:00 China time → ISO string. */
export function parseChinaDateInput(value?: string): string | undefined {
  if (!value?.trim()) return undefined;
  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return trimmed;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const ms = Date.UTC(year, month - 1, day, 0, 0, 0, 0) - CHINA_UTC_OFFSET_MS;
  return new Date(ms).toISOString();
}

export function formatChinaDateTime(
  value?: string | Date | null,
  locale = 'zh-CN'
): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(locale, {
    timeZone: CHINA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** YYYY-MM-DDTHH:mm for <input type="datetime-local"> in China time. */
export function formatChinaDateTimeLocal(value?: string | Date | null): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const { year, month, day, hour, minute } = toChinaCalendarParts(date);
  return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}`;
}

/** Parse YYYY-MM-DDTHH:mm as China time → ISO string. */
export function parseChinaDateTimeLocal(value?: string): string | undefined {
  if (!value?.trim()) return undefined;
  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(trimmed);
  if (!match) return trimmed;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const ms =
    Date.UTC(year, month - 1, day, hour, minute, 0, 0) - CHINA_UTC_OFFSET_MS;
  return new Date(ms).toISOString();
}
